import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Paper, Collapse, Box, Typography, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";
import jwtDecode from "jwt-decode";
import * as api from "../forecastApi";
import ChatHeader from "./ChatHeader";
import ChatMessageList from "./ChatMessageList";
import ChatComposer from "./ChatComposer";

const useStyles = makeStyles((theme) => ({
  floating: {
    position: "fixed",
    right: 24,
    bottom: 24,
    width: 360,
    zIndex: 1300,
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  collapsedFloating: {
    width: 280,
  },
  inlineError: {
    color: "#c62828",
    fontSize: "0.75rem",
    padding: theme.spacing(0, 1.5, 1, 1.5),
  },
}));

const decodeToken = () => {
  try {
    return jwtDecode(localStorage.authToken);
  } catch {
    return {};
  }
};

const latestSyncIdFromList = (rows) => {
  if (!rows || rows.length === 0) return null;
  return rows.reduce(
    (max, r) =>
      r.collabrixForecastSyncID > max ? r.collabrixForecastSyncID : max,
    0
  );
};

export default function ForecastChat({ hasFetched, forecastList, filters }) {
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState(true);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const token = useMemo(decodeToken, []);
  const userDetails = useSelector((state) => state.user.userDetails || {});
  const currentEmail = userDetails.email_address || "";
  let collaboratorTypeID = token.collaborator_type_id || null; // Default fallback
       

  const syncId = useMemo(
    () => latestSyncIdFromList(forecastList),
    [forecastList]
  );

  const fetchMessages = useCallback(async (signal) => {
    if (!syncId || !filters.cmoOrgID) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.fetchForecastChatData(
        { collabrixForecastSyncID: syncId, cmoOrgID: filters.cmoOrgID },
        signal
      );
      const desc = (res && res.status && res.data) || [];
      setMessages([...desc].reverse());
    } catch {
      setError("Something went wrong. Please try again.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [syncId, filters.cmoOrgID]);

  useEffect(() => {
    if (collapsed || !syncId || !filters.cmoOrgID) return undefined;
    const ctrl = new AbortController();
    fetchMessages(ctrl.signal);
    return () => ctrl.abort();
  }, [collapsed, syncId, filters.cmoOrgID, fetchMessages]);

  const onSend = async (text) => {
    if (!syncId) return;
    setSending(true);
    setError("");
    const payload = {
      collabrixForecastSyncID: syncId,
      cmoOrgID: filters.cmoOrgID,
      sponsorOrgID: filters.sponsorOrgID,
      chatData: text,
      chatSenderEmail: currentEmail,
      chatTimeStamp: new Date().toISOString(),
      collaboratorTypeID,
    };
    try {
      const res = await api.addForecastChatData(payload);
      if (res && res.status) {
        await fetchMessages();
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!hasFetched) return null;

  return (
    <Paper
      className={`${classes.floating} ${
        collapsed ? classes.collapsedFloating : ""
      }`}
      elevation={6}
    >
      <ChatHeader
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        title="Comments Section"
      />
      <Collapse in={!collapsed} unmountOnExit>
        <Box>
          <ChatMessageList
            messages={messages}
            loading={loading}
            currentEmail={currentEmail}
          />
          {error && (
            <Typography className={classes.inlineError}>{error}</Typography>
          )}
          <ChatComposer
            disabled={!syncId}
            sending={sending}
            onSend={onSend}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}
